const CUSTOM_SUFFIX = process.env.REACT_APP_GOBODO_CUSTOM_SUFFIX ?? '__c';

function extractIdFromLookupishValue(v: unknown): string {
  if (v == null) return '';
  if (typeof v === 'object' && v !== null) {
    const obj = v as Record<string, unknown>;
    if ('relatedObject' in obj) {
      const ro = (obj as { relatedObject?: { id?: unknown; key?: unknown } }).relatedObject;
      if (ro?.key != null) return String(ro.key).trim();
      if (ro?.id != null) return String(ro.id).trim();
    }
  }
  const s = String(v).trim();
  return s;
}

/**
 * Case id/key linked to a Task (Gobodo uses `task_related_to{suffix}`; app/mock may use RelatedTo__c).
 * Matches values already flattened by `convertFromGobodoRecord` (lookup → string key/id).
 */
export function extractTaskRelatedCaseId(task: Record<string, unknown>): string {
  const d = (task.data as Record<string, unknown>) ?? task;
  const skin = `task_related_to${CUSTOM_SUFFIX}`;
  const fromSkin = extractIdFromLookupishValue(d[skin] ?? task[skin]);
  if (fromSkin) return fromSkin;
  const fallbacks = [
    d.RelatedTo__c,
    task.RelatedTo__c,
    d.RelatedTo,
    task.RelatedTo,
    d.relatedTo,
    task.relatedTo,
    d.task_related_to__c,
    task.task_related_to__c,
  ];
  for (const v of fallbacks) {
    const x = extractIdFromLookupishValue(v);
    if (x) return x;
  }
  return '';
}

/** Task→User assignee skin API name; override if your Gobodo schema differs. */
function taskAssigneeRelSkinKey(): string {
  const fromEnv = process.env.REACT_APP_GOBODO_TASK_ASSIGNEE_REL_FIELD?.trim();
  if (fromEnv) return fromEnv;
  return `AssigneeRelField${CUSTOM_SUFFIX}`;
}

/**
 * Task reassignment from SearchableSelect: display name on Assignee/Owner text fields; when pick is `u:{graphUserKey}`,
 * also sets the Task assignee relationship skin (default `AssigneeRelField{suffix}`, same pattern as Case OwnerRelField).
 */
export function applyTaskAssigneeFromPicker(
  payload: Record<string, unknown>,
  options: { assigneePickRaw?: string; assigneeDisplayName: string }
): void {
  const name = options.assigneeDisplayName.trim();
  payload.Assignee__c = name;
  payload.assignee = name;
  payload.Owner__c = name;
  payload.owner = name;
  const raw = options.assigneePickRaw?.trim() ?? '';
  const uid = raw.startsWith('u:') ? raw.slice(2).trim() : '';
  if (!uid) return;
  payload[taskAssigneeRelSkinKey()] = uid;
}

/**
 * Gobodo Task lookups (Michael / feature/task_schema): task_related_to*, ask_parent_task_rel*.
 * Values are graph keys (e.g. Case id). Parent task optional for follow-on work.
 */
export function applyCreditBlockTaskRelationFields(
  taskPayload: Record<string, unknown>,
  relatedRecordId: string,
  parentTaskId?: string
): void {
  const relKey = `task_related_to${CUSTOM_SUFFIX}`;
  const parentKey = `ask_parent_task_rel${CUSTOM_SUFFIX}`;
  taskPayload[relKey] = relatedRecordId;
  if (parentTaskId?.trim()) {
    taskPayload[parentKey] = parentTaskId.trim();
  }
}
